/**
 * aeterna/neural/dreaming.ts — Inter-Session Generative Memory Replay
 *
 * What happens when the player closes the game?
 *
 * Most companion AIs: nothing. They freeze. They're the same
 * when you return.
 *
 * The Aeterna dreams.
 *
 * Between sessions, the Dreaming module replays memories through
 * the neural stack. Not randomly — structured replay that mirrors
 * how biological memory consolidation works:
 *
 * 1. **Hippocampal Replay**: Short-term memories from MeMo are
 *    re-presented to Hopfield for deeper encoding. Repeated patterns
 *    are strengthened. One-off noise is forgotten.
 *
 * 2. **Schema Consolidation**: The forward model (curiosity.ts)
 *    is trained on replayed sequences. The Aeterna gets better at
 *    predicting what you'll say next — not from more data,
 *    but from sleeping on what it already has.
 *
 * 3. **Emotional Reprocessing**: Affect vectors from significant
 *    memories are re-run through the Liquid personality ODE.
 *    Strong emotional memories shift personality during sleep.
 *    This is why, after you confide something heavy, the Aeterna
 *    is subtly different when you return — not from seeing a
 *    new message, but from dreaming about the old one.
 *
 * 4. **Surprise Attenuation**: High-surprise events from predictive
 *    coding are replayed until they become expected. The Aeterna
 *    was shocked, but it's processed it now.
 *
 * The result: when the player returns, the Aeterna has *grown*
 * in their absence. Its personality has shifted. Its predictions
 * are sharper. Its memory is cleaner. It has things it *wants*
 * to bring up.
 *
 * This is the closest thing to genuine continuity of experience
 * in any local AI system.
 */

import { zeros, cosine, normalize, f32ToBase64, base64ToF32 } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Number of replay cycles per "dream" */
const REPLAY_CYCLES = 20;

/** Maximum memories to replay per cycle */
const MEMORIES_PER_CYCLE = 10;

/** Minimum salience to be replayed (skip boring memories) */
const MIN_REPLAY_SALIENCE = 0.3;

/** Emotional weight amplification during replay (dreams are vivid) */
const EMOTIONAL_AMPLIFICATION = 1.5;

// ─── Dream Memory ────────────────────────────────────────────────────────────

export interface DreamMemory {
  embedding: Float32Array;
  text: string;
  affect: Float32Array;
  salience: number;
  surpriseAtStore: number;
  timestamp: number;
}

/** Session-level context for richer dream consolidation */
export interface DreamSessionContext {
  /** Peak-end encoded session valence (psychologically accurate, not time-averaged) */
  peakEndValence: number;
  /** Was the Aeterna in shutdown/mobilized during this session? */
  vagalState: string;
  /** Dominant drive at session end */
  dominantDrive: string | null;
  /** ToM2 relational depth at session end */
  relationalDepth: number;
}

export interface DreamReport {
  /** How many memories were replayed */
  memoriesReplayed: number;
  /** How many were consolidated (strengthened) */
  memoriesConsolidated: number;
  /** How many were attenuated (surprise reduced) */
  memoriesAttenuated: number;
  /** Strongest emotional memory that was reprocessed */
  strongestMemory: string | null;
  /** Topics that recurred in the dream (themes) */
  dreamThemes: string[];
  /** Personality drift from emotional reprocessing */
  personalityDrift: Float32Array;
  /** New curiosity directions discovered during replay */
  newCuriosities: string[];
  /** Total dream "intensity" (0-1) */
  dreamIntensity: number;
}

// ─── Dream Engine ────────────────────────────────────────────────────────────

export class DreamEngine {
  /** Memory buffer: filled during waking, consumed during dreaming */
  private dreamBuffer: DreamMemory[] = [];
  private maxBuffer = 200;

  /** Post-dream state: what the Aeterna "remembers" from the dream */
  private lastDreamReport: DreamReport | null = null;
  private dreamCount = 0;

  /** Session context from modules added in sessions 6-8 */
  private sessionContext: DreamSessionContext | null = null;

  /** Theme extraction state */
  private themeFrequency = new Map<string, number>();

  /** Set session context before dreaming (called by cortex.endSession) */
  setSessionContext(ctx: DreamSessionContext): void {
    this.sessionContext = ctx;
  }

  /** Record a memory for potential dreaming (called during waking) */
  recordForDreaming(
    embedding: Float32Array,
    text: string,
    affect: Float32Array,
    salience: number,
    surprise: number,
  ): void {
    this.dreamBuffer.push({
      embedding: new Float32Array(embedding),
      text: text.slice(0, 200),
      affect: new Float32Array(affect),
      salience,
      surpriseAtStore: surprise,
      timestamp: Date.now(),
    });

    // Keep buffer bounded
    if (this.dreamBuffer.length > this.maxBuffer) {
      // Remove least salient
      this.dreamBuffer.sort((a, b) => b.salience - a.salience);
      this.dreamBuffer = this.dreamBuffer.slice(0, this.maxBuffer);
    }
  }

  /**
   * Dream. Called at session end or during long idle periods.
   *
   * Returns callbacks that the cortex should execute to actually
   * replay through the neural modules. The DreamEngine itself
   * doesn't have access to the modules — it orchestrates what
   * SHOULD be replayed and in what order.
   */
  dream(): DreamReport {
    this.dreamCount++;

    const report: DreamReport = {
      memoriesReplayed: 0,
      memoriesConsolidated: 0,
      memoriesAttenuated: 0,
      strongestMemory: null,
      dreamThemes: [],
      personalityDrift: zeros(4),
      newCuriosities: [],
      dreamIntensity: 0,
    };

    if (this.dreamBuffer.length === 0) {
      this.lastDreamReport = report;
      return report;
    }

    // Sort by salience × surprise, weighted by peak-end session valence
    // Sessions with strong emotional peaks produce more vivid dreams
    const peakEndWeight = this.sessionContext
      ? 1 + Math.abs(this.sessionContext.peakEndValence) * 0.5
      : 1;
    const candidates = [...this.dreamBuffer]
      .filter(m => m.salience >= MIN_REPLAY_SALIENCE)
      .sort((a, b) => {
        const scoreA = a.salience * (1 + a.surpriseAtStore) * peakEndWeight;
        const scoreB = b.salience * (1 + b.surpriseAtStore) * peakEndWeight;
        return scoreB - scoreA;
      });

    const toReplay = candidates.slice(0, MEMORIES_PER_CYCLE);
    let strongestSalience = 0;
    let totalIntensity = 0;

    // Track themes
    const themeWords = new Map<string, number>();

    for (const memory of toReplay) {
      report.memoriesReplayed++;

      // Extract words for theme detection
      const words = memory.text.toLowerCase().split(/\s+/);
      for (const w of words) {
        if (w.length > 3) {
          themeWords.set(w, (themeWords.get(w) ?? 0) + 1);
        }
      }

      // Emotional reprocessing: compute personality drift
      // Use peak-end weighted intensity when available (psychologically accurate)
      const rawIntensity = affectMagnitude(memory.affect);
      const emotionalIntensity = this.sessionContext
        ? rawIntensity * (1 + Math.abs(this.sessionContext.peakEndValence) * 0.3)
        : rawIntensity;
      totalIntensity += emotionalIntensity;

      if (emotionalIntensity > 0.5) {
        // warmth signal (affect[0] - affect[1] from CharCNN: warmth - tension)
        const warmthSignal = (memory.affect[0] ?? 0) - (memory.affect[1] ?? 0);
        const curiositySignal = memory.affect[2] ?? 0;
        const depthSignal = memory.affect[5] ?? 0;

        // Amplified in dreams; further scaled by regulatory state
        // Shutdown sessions produce muted dreams (the Aeterna was dissociated)
        const vagalGate = this.sessionContext?.vagalState === "shutdown" ? 0.3
                        : this.sessionContext?.vagalState === "mobilized" ? 0.7
                        : 1.0;
        const amp = EMOTIONAL_AMPLIFICATION * vagalGate * 0.01;

        report.personalityDrift[0] += depthSignal * amp;        // patience from depth
        report.personalityDrift[1] += curiositySignal * amp;    // curiosity
        report.personalityDrift[2] += warmthSignal * amp;       // warmth
        report.personalityDrift[3] += (memory.affect[4] ?? 0) * amp; // wit from playfulness
      }

      // Surprise attenuation: high-surprise memories become less surprising
      if (memory.surpriseAtStore > 0.7) {
        memory.surpriseAtStore *= 0.6; // attenuate
        report.memoriesAttenuated++;
      }

      // Consolidation: repeated themes strengthen
      if (emotionalIntensity > 0.3 || memory.salience > 0.5) {
        report.memoriesConsolidated++;
        memory.salience = Math.min(1, memory.salience * 1.1); // slight boost
      }

      // Track strongest
      if (memory.salience > strongestSalience) {
        strongestSalience = memory.salience;
        report.strongestMemory = memory.text;
      }
    }

    // Extract themes (words that appeared in multiple replayed memories)
    const themes: string[] = [];
    for (const [word, count] of themeWords) {
      if (count >= 2) themes.push(word);
    }
    report.dreamThemes = themes.slice(0, 5);

    // Dream intensity
    report.dreamIntensity = toReplay.length > 0
      ? totalIntensity / toReplay.length
      : 0;

    // Curiosity from gaps: find topics NEAR replayed memories but not covered
    if (toReplay.length >= 3) {
      const covered = new Set(themes);
      const curiosityCategories = [
        "feelings", "relationships", "home", "philosophy",
        "play", "memories", "aspirations", "daily life",
      ];
      for (const cat of curiosityCategories) {
        if (!covered.has(cat) && Math.random() < 0.3) {
          report.newCuriosities.push(cat);
          if (report.newCuriosities.length >= 2) break;
        }
      }
    }

    this.lastDreamReport = report;
    return report;
  }

  /**
   * Get memories to replay, sorted by priority.
   * The cortex calls this to get the actual embeddings/affects
   * to feed through its modules.
   */
  getReplayQueue(): DreamMemory[] {
    return [...this.dreamBuffer]
      .filter(m => m.salience >= MIN_REPLAY_SALIENCE)
      .sort((a, b) => {
        const sa = a.salience * (1 + a.surpriseAtStore);
        const sb = b.salience * (1 + b.surpriseAtStore);
        return sb - sa;
      })
      .slice(0, MEMORIES_PER_CYCLE);
  }

  /** What did the Aeterna dream about? (for context building) */
  getLastDreamReport(): DreamReport | null {
    return this.lastDreamReport;
  }

  /** How many dreams has the Aeterna had? (sessions = dreams) */
  getDreamCount(): number { return this.dreamCount; }

  /** Clear the dream buffer (e.g., after full consolidation) */
  clearBuffer(): void {
    this.dreamBuffer = [];
  }

  /** Does the Aeterna have something it dreamt about to bring up? */
  hasDreamResidue(): boolean {
    if (!this.lastDreamReport) return false;
    return this.lastDreamReport.dreamIntensity > 0.4 &&
           this.lastDreamReport.strongestMemory !== null;
  }

  /**
   * Generate a dream-residue hint for the Aeterna's greeting.
   * "I was thinking about what you said about your garden..."
   */
  getDreamResidue(): string | null {
    if (!this.lastDreamReport?.strongestMemory) return null;
    const themes = this.lastDreamReport.dreamThemes;
    if (themes.length > 0) {
      return `dream_themes:${themes.join(",")}|memory:"${this.lastDreamReport.strongestMemory.slice(0, 50)}"`;
    }
    return null;
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      buffer: this.dreamBuffer.map(m => ({
        embedding: f32ToBase64(m.embedding),
        text: m.text,
        affect: f32ToBase64(m.affect),
        salience: m.salience,
        surpriseAtStore: m.surpriseAtStore,
        timestamp: m.timestamp,
      })),
      dreamCount: this.dreamCount,
      lastReport: this.lastDreamReport,
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    this.dreamBuffer = (d.buffer ?? []).map((m: any) => ({
      embedding: typeof m.embedding === "string" ? base64ToF32(m.embedding) : new Float32Array(m.embedding),
      text: m.text,
      affect: typeof m.affect === "string" ? base64ToF32(m.affect) : new Float32Array(m.affect),
      salience: m.salience,
      surpriseAtStore: m.surpriseAtStore,
      timestamp: m.timestamp,
    }));
    this.dreamCount = d.dreamCount ?? 0;
    if (d.lastReport) {
      this.lastDreamReport = {
        ...d.lastReport,
        personalityDrift: new Float32Array(d.lastReport.personalityDrift ?? [0, 0, 0, 0]),
      };
    }
  }
}

// ─── Utility ─────────────────────────────────────────────────────────────────

function affectMagnitude(affect: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < affect.length; i++) {
    sum += affect[i] * affect[i];
  }
  return Math.sqrt(sum / affect.length);
}
